import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue'

import { useChats } from '@communities/chats-layer/composables/useChats'
import { useAuth } from '@common/auth-layer/composables/useAuth'
import type { INotification } from '@common/notification-layer/types/notification.types'

import { useEventsQueryStore } from '../stores/events.query-store'
import { asPositiveInteger, toConnectionNotificationState, toNotificationConnectionIds } from '../utils/event-connections.utils'
import type { EventConnectionStatus } from '../types/events-management'
import type { UseEventConnectionNotification } from '../types/use-event-connections'

/** Live status and decisions for one Event connection-request notification in the drawer. Failures propagate to the global error handler. */
export const useEventConnectionNotification = (
  notification: MaybeRefOrGetter<INotification>,
  notifications: MaybeRefOrGetter<readonly INotification[]>
): UseEventConnectionNotification => {
  /*
   * Viewer-scoped connection data. The endpoints resolve the viewer from auth, so `contactId` is
   * never sent -- it is only the cache dimension that keeps one viewer's actions out of another's.
   */
  const auth = useAuth()
  const contactId = computed(() => {
    const authContactId = auth.store.contactId
    if (authContactId && typeof authContactId === 'string' && authContactId !== '[object Object]') {
      return authContactId
    }
    if (process.dev) {
      console.warn('[useEventConnectionNotification] Contact ID not available:', authContactId)
    }
    return null
  })
  const queryStore = useEventsQueryStore(contactId)
  const chats = useChats()

  const connectionId = computed(() => toValue(notification).metaData?.connectionId?.trim() ?? '')
  // The stored notification carries requestVersion as a string ("1"); the status API returns a number.
  const notificationRequestVersion = computed(() => asPositiveInteger(toValue(notification).metaData?.requestVersion) ?? undefined)
  const connectionIds = computed(() => toNotificationConnectionIds(connectionId.value, toValue(notifications)))
  const statusesQuery = queryStore.fetchEventConnectionStatusesQuery(connectionIds)

  // A decision made here shows at once, before the invalidated status query refetches.
  const locallyResolved = ref<EventConnectionStatus>()
  const connection = computed(
    () => locallyResolved.value ?? statusesQuery.data.value?.find(status => status.connectionId === connectionId.value)
  )
  const state = computed(() => toConnectionNotificationState(connection.value, notificationRequestVersion.value))
  const isChecking = computed(() => statusesQuery.isLoading.value)
  const isUnavailable = computed(() => Boolean(statusesQuery.error.value))
  const isResolving = ref(false)

  const decide = async (decision: 'accept' | 'decline') => {
    const requestVersion = notificationRequestVersion.value ?? connection.value?.requestVersion
    if (isResolving.value || !connectionId.value || !requestVersion) return
    isResolving.value = true
    try {
      const params = { connectionId: connectionId.value, requestVersion }
      if (decision === 'accept') {
        const result = await queryStore.acceptEventConnection(params)
        locallyResolved.value = {
          ...params,
          status: 'accepted',
          direction: 'incoming',
          requestVersion: result.requestVersion,
          conversationId: result.conversationId,
        }
        await chats.openConversationById(result.conversationId)
      } else {
        const result = await queryStore.declineEventConnection(params)
        locallyResolved.value = { ...params, status: 'declined', direction: 'incoming', requestVersion: result.requestVersion }
      }
    } finally {
      isResolving.value = false
    }
  }

  const openMessage = async () => {
    const conversationId = connection.value?.conversationId?.trim()
    if (!conversationId) return
    await chats.openConversationById(conversationId)
  }

  return {
    state,
    connection,
    isChecking,
    isUnavailable,
    isResolving,
    accept: () => decide('accept'),
    decline: () => decide('decline'),
    openMessage,
  }
}
